<template>
  <div class="logging-table" v-if="getLogEntries">
    <DataTable
      class="p-datatable-sm p-datatable-striped p-datatable-gridlines"
      :value="getLogEntries"
      :loading="isLoading"
      :rowHover="true"
      :lazy="true"
      filterDisplay="row"
      :stateKey="tableStateStore.filterName()"
      :filters.sync="tableStateStore.filters"
      :rows.sync="tableStateStore.pagination.pageSize"
      :sortField="tableStateStore.sorting.sortField"
      :sortOrder="tableStateStore.sorting.sortOrder"
      :first="tableStateStore.skip()"
      stateStorage="local"
      :totalRecords="getLogEntryCount"
      :paginator="true"
      paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
      :currentPageReportTemplate="
        $t('show') +
          ' {first} ' +
          $t('to') +
          ' {last} ' +
          $t('of') +
          ' {totalRecords} ' +
          $t('records')
      "
      :rowsPerPageOptions="[10, 20, 50, 100]"
      @page="onPage($event)"
      @sort="onSort($event)"
      @filter="onFilter($event)"
      @state-restore="onStateRestore($event)"
      responsiveLayout="scroll"
    >
      <template #header>
        <div class="table-header">
          <div class="table-header-left">
            <h4 class="page-title">{{ $t('logging') }}</h4>
          </div>
          <div class="table-header-right">
            <button class="btn btn-sm btn-inverse" @click.prevent="onRemoveFilters()">
              <i class="fa fa-remove mr-2"></i>
              {{ $t('remove_filters') }}
            </button>
          </div>
        </div>
      </template>

      <template #empty>
        {{ $t('no_data_found') }}
      </template>
      <template #loading>
        {{ $t('loading') }}
      </template>

      <!-- Timestamp -->
      <Column
        field="timestamp"
        :header="$t('timestamp')"
        sortable
        filterField="timestamp"
        :showFilterMenu="false"
      >
        <template #body="slotProps">
          <span v-if="slotProps.data && slotProps.data.timestamp">
            {{ formatDate(slotProps.data.timestamp) }}
          </span>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <Calendar
            v-model="filterModel.range"
            @input="filterCallback()"
            selectionMode="range"
            class="p-column-filter"
            dateFormat="dd.mm.yy"
            :placeholder="$t('date')"
            :showWeek="true"
            :yearNavigator="true"
            :monthNavigator="true"
          />
        </template>
      </Column>

      <!-- Event -->
      <Column
        field="event"
        :header="$t('event')"
        sortable
        filterField="event"
        :showFilterMenu="false"
      >
        <template #body="slotProps">
          <span v-if="slotProps.data && slotProps.data.event">
            {{ slotProps.data.event }}
          </span>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>

      <!-- user -->
      <Column field="user" :header="$t('user')" sortable filterField="user" :showFilterMenu="false">
        <template #body="slotProps">
          <span v-if="slotProps.data && slotProps.data.user">
            {{ slotProps.data.user.firstname }} {{ slotProps.data.user.lastname }}<br />
            <small>{{ slotProps.data.user.email }}</small>
          </span>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>

      <!-- Data -->
      <Column field="data" :header="$t('data')">
        <template #body="slotProps">
          <span v-if="slotProps.data && slotProps.data.data">
            <pre class="data-preview">{{ formatData(slotProps.data.data) }}</pre>
          </span>
        </template>
      </Column>

      <!-- Device Info -->
      <Column
        field="deviceInfo"
        :header="$t('deviceInfo')"
        :headerStyle="{ width: '200px' }"
        :bodyStyle="'text-align: left; width: 200px'"
      >
        <template #body="slotProps">
          <span v-if="slotProps.data && slotProps.data.deviceInfo">
            <div v-if="slotProps.data.deviceInfo.model">
              <strong>{{ $t('model') }}:</strong> {{ slotProps.data.deviceInfo.model }}
            </div>
            <div v-if="slotProps.data.deviceInfo.platform">
              <strong>{{ $t('platform') }}:</strong> {{ slotProps.data.deviceInfo.platform }}
            </div>
            <div v-if="slotProps.data.deviceInfo.osVersion">
              <strong>{{ $t('osVersion') }}:</strong> {{ slotProps.data.deviceInfo.osVersion }}
            </div>
            <div v-if="slotProps.data.deviceInfo.appVersion">
              <strong>{{ $t('appVersion') }}:</strong> {{ slotProps.data.deviceInfo.appVersion }}
            </div>
          </span>
          <span v-else>-</span>
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import InputText from 'primevue/inputtext';
import Calendar from 'primevue/calendar';

export default {
  name: 'LoggingTable',
  components: {
    DataTable,
    Column,
    InputText,
    Calendar,
  },
  data() {
    return {
      loggingTableStateConfig: {
        name: 'logging',
        filters: {
          event: { value: null },
          user: { value: null },
          timestamp: { range: null },
        },
        customFilters: {},
        sorting: { sortField: 'timestamp', sortOrder: -1 }, // Latest entries first
      },
      defaultFilters: {
        event: { value: null },
        user: { value: null },
        timestamp: { range: null },
      },
    };
  },
  computed: {
    ...mapGetters(['getLogEntries', 'getLogEntryCount']),
    ...mapGetters(['isLoading', 'getTableState', 'getEnumValues']),

    tableStateStore() {
      return this.getTableState(this.loggingTableStateConfig.name);
    },
  },
  methods: {
    ...mapActions(['fetchLogEntriesPaginated']),
    ...mapActions(['initTableState', 'fetchEnumValues']),

    /**
     * Load remote table data
     */
    async loadTableData() {
      await this.fetchLogEntriesPaginated({
        pagination: {
          skip: this.tableStateStore.skip(),
          pageSize: this.tableStateStore.pagination.pageSize,
        },
        sorting: this.tableStateStore.sorting,
        filters: this.tableStateStore.filters,
      });
    },

    /**
     * Load more results from server or cache on pagination click
     */
    async onPage(event) {
      this.tableStateStore.pagination = { page: event.page, pageSize: event.rows };
      this.loadTableData();
    },

    /**
     * Load results from server/cache on sort
     */
    async onSort(event) {
      this.tableStateStore.sorting = { sortField: event.sortField, sortOrder: event.sortOrder };
      this.loadTableData();
    },

    /**
     * Load results from server/cache on filter
     */
    async onFilter(event) {
      this.loadTableData();
    },

    /**
     * Trigger when state is loaded from local storage
     */
    async onStateRestore(event) {
      this.tableStateStore.restore(event);
    },

    /**
     * Reset filter settings
     */
    async onRemoveFilters(event) {
      this.tableStateStore.reset();
      await this.loadTableData();
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString('de-DE');
    },

    formatData(data) {
      if (!data) return '-';
      try {
        return JSON.stringify(data, null, 2);
      } catch (error) {
        return String(data);
      }
    },
  },
  async created() {
    await this.initTableState(this.loggingTableStateConfig);
  },

  async mounted() {
    await this.loadTableData();
  },
};
</script>

<style lang="scss" scoped>
.data-preview {
  font-size: 0.8em;
  max-height: 250px;
  overflow-y: auto;
  white-space: pre-wrap;
  word-break: break-word;
  background-color: #f8f9fa;
  padding: 4px;
  border-radius: 4px;
  margin: 0;
}

/** Fix Dropdown overlay and table height */
.p-datatable-responsive-scroll > .p-datatable-wrapper {
  min-height: 37rem;
}

.p-column-filter-clear-button {
  display: none;
  width: 0;
}

.p-column-filter-menu-button {
  display: none;
  width: 0;
}
</style>
